import { assertNever } from "@redotech/util/type";
import * as z from "zod";
import {
  ANALYTICS_QUERY_LIMITS,
  AnalyticsNameKind,
} from "./analytics-query-constants";
import type {
  AnalyticsCatalogRef,
  AnalyticsLiftedName,
  AnalyticsLiftedParent,
  AnalyticsLocalRef,
  AnalyticsNameSlot,
} from "./analytics-query-types";

/**
 * Snake case with no dot (never a catalog `entity.member` key) and no lifted `__N` suffix.
 * Split by how the name ends, with no lookaround, so a strict-mode JSON Schema can carry it.
 */
export const ANALYTICS_LOCAL_NAME_PATTERN =
  /^[a-z](?:[a-z0-9_]*[a-z_])?$|^[a-z](?:[a-z0-9_]*[a-z])?[0-9]+$|^[a-z](?:[a-z0-9_]*[a-z0-9])?_[0-9]+$/;

export const ANALYTICS_LOCAL_NAME_MESSAGE =
  "a definition name is lowercase snake_case that starts with a letter, has no dot, and does not end in __ and a number";

export const ANALYTICS_LOCAL_ROUTE_MESSAGE =
  "only a catalog name takes a route; drop it here, or set it on the catalog ref inside the definition";

export const AnalyticsLocalNameSchema = z
  .string()
  .max(ANALYTICS_QUERY_LIMITS.nameMax)
  .regex(ANALYTICS_LOCAL_NAME_PATTERN, ANALYTICS_LOCAL_NAME_MESSAGE)
  .brand<"AnalyticsLocalName">();

export const AnalyticsMemberNameSchema = z
  .string()
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.nameMax)
  .brand<"AnalyticsMemberName">();

export const AnalyticsEntityNameSchema = z
  .string()
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.nameMax)
  .brand<"AnalyticsEntityName">();

export const AnalyticsRouteNameSchema = z
  .string()
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.nameMax)
  .brand<"AnalyticsRouteName">();

export const AnalyticsCatalogRefSchema = z.strictObject({
  kind: z.literal(AnalyticsNameKind.Catalog),
  entity: AnalyticsEntityNameSchema,
  member: AnalyticsMemberNameSchema,
});

export const AnalyticsLocalRefSchema = z.strictObject({
  kind: z.literal(AnalyticsNameKind.Local),
  name: AnalyticsLocalNameSchema,
});

export const AnalyticsValueNameSchema = z.discriminatedUnion("kind", [
  AnalyticsCatalogRefSchema,
  AnalyticsLocalRefSchema,
]);

export function analyticsLocalName(
  value: string,
): import("./analytics-query-types").AnalyticsLocalName {
  return AnalyticsLocalNameSchema.parse(value);
}

export function analyticsEntityName(
  value: string,
): import("./analytics-query-types").AnalyticsEntityName {
  return AnalyticsEntityNameSchema.parse(value);
}

export function analyticsRouteName(
  value: string,
): import("./analytics-query-types").AnalyticsRouteName {
  return AnalyticsRouteNameSchema.parse(value);
}

export function analyticsMemberName(
  value: string,
): import("./analytics-query-types").AnalyticsMemberName {
  return AnalyticsMemberNameSchema.parse(value);
}

export function analyticsCatalogRef(
  entity: string,
  member: string,
): AnalyticsCatalogRef {
  return AnalyticsCatalogRefSchema.parse({
    kind: AnalyticsNameKind.Catalog,
    entity,
    member,
  });
}

/** Local names the compiler builds, such as `entity.member@route`; no author writes them, so no author pattern applies. */
const AnalyticsCompilerLocalNameSchema = z
  .string()
  .min(1)
  .brand<"AnalyticsLocalName">();

export function analyticsCompilerLocalRef(key: string): AnalyticsLocalRef {
  return {
    kind: AnalyticsNameKind.Local,
    name: AnalyticsCompilerLocalNameSchema.parse(key),
  };
}

export function analyticsLocalRef(name: string): AnalyticsLocalRef {
  return AnalyticsLocalRefSchema.parse({ kind: AnalyticsNameKind.Local, name });
}

export function analyticsLiftedName(
  parent: AnalyticsLiftedParent,
  index: number,
): AnalyticsLiftedName {
  if (!Number.isInteger(index) || index < 1) {
    throw new Error(`A lifted index is a positive integer, not ${index}.`);
  }
  return { kind: AnalyticsNameKind.Lifted, parent, index };
}

export function isAnalyticsCatalogRef(
  name: AnalyticsNameSlot,
): name is AnalyticsCatalogRef<string, string> {
  return name.kind === AnalyticsNameKind.Catalog;
}

export function isAnalyticsLocalRef(
  name: AnalyticsNameSlot,
): name is AnalyticsLocalRef<string> {
  return name.kind === AnalyticsNameKind.Local;
}

/** Map key or column name. `analyticsNameKeyChain` and `analyticsCatalogKey` read it back. */
export function analyticsNameKey(name: AnalyticsNameSlot): string {
  switch (name.kind) {
    case AnalyticsNameKind.Catalog:
      return `${name.entity}.${name.member}`;
    case AnalyticsNameKind.Local:
      return name.name;
    case AnalyticsNameKind.Lifted:
      return `${analyticsNameKey(name.parent)}__${name.index}`;
    default:
      return assertNever(name);
  }
}

/** A lifted name key ends in `__` plus the positive index from `analyticsLiftedName`. */
const liftedNameKeySuffix = /^(.*)__\d+$/;

/** This key, then each parent it was lifted from. */
export function analyticsNameKeyChain(key: string): readonly string[] {
  const chain = [key];
  let current = key;
  let lifted = liftedNameKeySuffix.exec(current);
  while (lifted?.[1] !== undefined && lifted[1] !== current) {
    current = lifted[1];
    chain.push(current);
    lifted = liftedNameKeySuffix.exec(current);
  }
  return chain;
}

/** A catalog name key is `entity.member`. The first dot splits them. */
export function analyticsCatalogKey(
  key: string,
): { readonly entity: string; readonly member: string } | undefined {
  const dot = key.indexOf(".");
  if (dot <= 0) {
    return undefined;
  }
  return { entity: key.slice(0, dot), member: key.slice(dot + 1) };
}
